import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';
import type {
   TournamentsResponse,
   TournamentGamesResponse,
   TournamentFilterOptionsResponse,
} from '~/types/types';

export interface TournamentSearchFilters {
   club?: string;
   country?: string[];
   tournament?: string;
   start_date?: string;
   // Tournaments running on this date: start_date <= active_on <= end_date, inclusive.
   // Distinct from start_date, which means "starting on or after". Several tournaments
   // can overlap a single date, so this narrows the list rather than picking one row.
   active_on?: string;
   sortBy?: 'tournament' | 'club' | 'startDate' | 'tournamentHandicap';
   sortOrder?: 'asc' | 'desc';
   page?: number;
   perPage?: number;
}

export function usePublicTournaments(brand: string, filters: Ref<TournamentSearchFilters>) {
   const config = useRuntimeConfig();
   const baseUrl = config.public.lmsApiBaseurl;
   const headers = { 'X-LMS-Requester': config.public.siteDomain as string };

   const {
      data: tournamentsData,
      isLoading,
      isFetching,
      error,
      refetch,
   } = useQuery({
      queryKey: ['public-tournaments', brand, filters],
      queryFn: async ({ queryKey }): Promise<TournamentsResponse> => {
         const [, , searchFilters] = queryKey as [string, string, TournamentSearchFilters];

         const params: Record<string, string | number> = {
            brand,
         };

         if (searchFilters.page) {
            params.page = searchFilters.page;
         }

         if (searchFilters.perPage) {
            params.per_page = searchFilters.perPage;
         }

         if (searchFilters.club) {
            params.club = searchFilters.club;
         }

         if (searchFilters.country && searchFilters.country.length > 0) {
            params.country = searchFilters.country.join(',');
         }

         if (searchFilters.tournament) {
            params.tournament = searchFilters.tournament;
         }

         if (searchFilters.start_date) {
            params.start_date = searchFilters.start_date;
         }

         if (searchFilters.active_on) {
            params.active_on = searchFilters.active_on;
         }

         if (searchFilters.sortBy) {
            params.sort_by = searchFilters.sortBy;
         }

         if (searchFilters.sortOrder) {
            params.sort_order = searchFilters.sortOrder;
         }

         const queryString = new URLSearchParams(
            Object.entries(params).map(([k, v]) => [k, String(v)])
         ).toString();

         // eslint-disable-next-line no-undef
         return await $fetch<TournamentsResponse>(
            `${baseUrl}public/tournaments?${queryString}`,
            { headers }
         );
      },
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
   });

   return {
      tournamentsData,
      isLoading,
      isFetching,
      error,
      refetch,
   };
}

export function usePublicTournamentFilterOptions(brand: string) {
   const config = useRuntimeConfig();
   const baseUrl = config.public.lmsApiBaseurl;
   const headers = { 'X-LMS-Requester': config.public.siteDomain as string };

   const {
      data: filterOptions,
      isLoading,
      error,
   } = useQuery({
      queryKey: ['public-tournaments', 'options', brand],
      queryFn: async (): Promise<TournamentFilterOptionsResponse> => {
         // eslint-disable-next-line no-undef
         return await $fetch<TournamentFilterOptionsResponse>(
            `${baseUrl}public/tournaments/options?brand=${brand}`,
            { headers }
         );
      },
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
   });

   return {
      filterOptions,
      isLoading,
      error,
   };
}

export function fetchPublicTournamentGames(baseUrl: string, tournamentId: number, headers: Record<string, string>, params?: URLSearchParams): Promise<TournamentGamesResponse> {
   const queryString = params ? `?${params.toString()}` : '';
   // eslint-disable-next-line no-undef
   return $fetch<TournamentGamesResponse>(
      `${baseUrl}public/tournaments/${tournamentId}/games${queryString}`,
      { headers }
   );
}
